import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Select, Button, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { api } from '../utils/apiClient';
import { useUser } from '../contexts/UserContext';

const EditUserDialog = ({ user, onSubmit, onCancel }) => {
  const { user: currentUser } = useUser();
  const [form] = Form.useForm();
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if current user is SuperAdmin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const response = await api.get('/userprivileges');
        const privileges = response.data;
        const superAdmin = privileges.some(p => p.privilege_name === 'SuperAdmin');
        setIsSuperAdmin(superAdmin);
      } catch (error) {
        console.error('Failed to check SuperAdmin status:', error);
      }
    };
    checkSuperAdmin();
  }, []);

  // Fetch available roles and user's current roles
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        locked: user.locked,
        active: user.active,
      });

      // Fetch user's current roles
      fetchUserRoles();
      // Fetch available roles for the user's org
      fetchAvailableRoles();
    }
  }, [user, form]);

  const fetchUserRoles = async () => {
    if (!user || !user.id) return;
    try {
      const response = await api.get('/users');
      const userData = response.data.find(u => u.id === user.id);
      if (userData && userData.roles) {
        setSelectedRoles(userData.roles);
        form.setFieldsValue({
          role_ids: userData.roles.map(r => r.id)
        });
      }
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
    }
  };

  const fetchAvailableRoles = async () => {
    if (!user) return;
    try {
      const orgId = user.org_id || currentUser?.org_id;

      if (!orgId) {
        console.error('No org_id available');
        return;
      }

      const response = await api.get(`/roles?org_id=${orgId}`);
      setAvailableRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch available roles:', error);
      setAvailableRoles([]);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const updatedUser = {
        ...user,
        first_name: values.first_name,
        last_name: values.last_name,
        locked: values.locked,
        active: values.active,
        role_ids: values.role_ids || []
      };
      await onSubmit(updatedUser);
      form.resetFields();
    } catch (error) {
      message.error('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Modal
      title="Edit User"
      open={true}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="First Name"
          name="first_name"
          rules={[{ required: true, message: 'Please enter first name' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Enter first name"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="last_name"
          rules={[{ required: true, message: 'Please enter last name' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Enter last name"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Roles"
          name="role_ids"
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            size="large"
            options={availableRoles.map(role => ({
              label: role.name,
              value: role.id
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Active"
          name="active"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Locked"
          name="locked"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserDialog;
